import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading, SectionTitle, GradeBadge, scoreTone } from "@/components/common";
import { ClassCreateDialog, ClassCard } from "@/pages/TeacherDashboard";
import { WeeklyChart } from "@/pages/StudentDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Link2, ArrowRight, Plus, Flame, Star } from "lucide-react";
import { toast } from "sonner";

export default function ParentDashboard() {
  const { user, refresh } = useAuth();
  const [kids, setKids] = useState(null);
  const [classes, setClasses] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/parent/children").then(({ data }) => setKids(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const link = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/parent/link", { code });
      toast.success(`${data.name} is now linked to your account`);
      setCode("");
      await refresh();
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!kids) return <Loading />;

  return (
    <div>
      <PageHeader eyebrow="Parent" title={`Hello, ${user.name.split(" ")[0]}`} subtitle="Follow your children's learning, and set up a home school class to assign lessons." />

      <div className="bg-white border border-border rounded-[14px] p-5 mb-8 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2"><Link2 className="h-4 w-4 text-[#0F7B7D]" />Link a child</div>
          <p className="text-sm text-muted-foreground mt-1">Ask your child for the parent link code shown on their dashboard.</p>
        </div>
        <form onSubmit={link} className="flex gap-2 md:w-[380px]">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Link code" className="h-11 uppercase" data-testid="parent-link-code-input" />
          <Button type="submit" className="h-11" disabled={busy} data-testid="parent-link-button">Link</Button>
        </form>
      </div>

      <SectionTitle>My children</SectionTitle>
      {kids.length === 0 ? (
        <EmptyState icon={Users} title="No children linked yet" text="Your child can create a student account, then share their link code with you." testid="parent-children-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6" data-testid="parent-children-list">
          {kids.map((k) => (
            <div key={k.user.user_id} className="bg-white border border-border rounded-[18px] p-6 lift" data-testid="parent-child-card">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[#E9E1CF] font-display font-semibold text-lg flex items-center justify-center">{k.user.name.slice(0, 1)}</div>
                <div className="flex-1">
                  <div className="font-display font-semibold text-lg">{k.user.name}</div>
                  <GradeBadge grade={k.user.grade_band} />
                </div>
                <Button variant="outline" className="bg-white" onClick={() => navigate(`/children/${k.user.user_id}`)} data-testid="parent-view-child-button">View progress <ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-5 text-center" data-testid="parent-child-progress">
                <div><div className="font-display text-xl font-semibold flex items-center justify-center gap-1"><Star className="h-4 w-4 text-[#F2B705]" />{k.user.xp}</div><div className="text-xs text-muted-foreground">XP</div></div>
                <div><div className="font-display text-xl font-semibold flex items-center justify-center gap-1"><Flame className="h-4 w-4 text-[#F26B6B]" />{k.user.streak}</div><div className="text-xs text-muted-foreground">Streak</div></div>
                <div><div className="font-display text-xl font-semibold">{k.lessons_completed}</div><div className="text-xs text-muted-foreground">Lessons</div></div>
                <div><div className={`font-display text-xl font-semibold ${scoreTone(k.average_score)}`}>{k.average_score != null ? `${k.average_score}%` : "—"}</div><div className="text-xs text-muted-foreground">Avg score</div></div>
              </div>
              <div className="mt-4"><WeeklyChart data={k.weekly} testid="parent-child-weekly" /></div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        <SectionTitle action={<Button size="sm" onClick={() => setOpen(true)} data-testid="parent-new-class-button"><Plus className="h-4 w-4 mr-1" />New class</Button>}>Home school classes</SectionTitle>
        {classes.length === 0 ? (
          <EmptyState title="Assign lessons to your kids" text="Create a home school class, have your children join with its code, then assign lessons and see their scores." action={<Button variant="outline" className="bg-white" onClick={() => setOpen(true)}>Create a class</Button>} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="parent-classes-list">{classes.map((c) => <ClassCard key={c.class_id} c={c} />)}</div>
        )}
      </div>
      <div className="mt-8 text-sm text-muted-foreground">Want to build your own curriculum? <Link to="/create" className="underline">Create courses and AI lessons</Link>.</div>
      <ClassCreateDialog open={open} onOpenChange={setOpen} label="New home school class" onCreated={(c) => { setOpen(false); navigate(`/classes/${c.class_id}`); }} />
    </div>
  );
}
